import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { FeatureProperties, KeyValueDistricts } from '../../../models/geo-data';
import { DataLayer } from '../../../models/data-layer';
import { filterFeaturesBySearchWordAsArray } from '../../../stores/geo/geo-utils';
import { ChipModule } from 'primeng/chip';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { WidgetStore } from '../../../stores/grid/widget.state';

@Component({
  selector: 'app-poi-list',
  standalone: true,
  imports: [ChipModule, ButtonModule, TooltipModule],
  templateUrl: './poi-list.component.html',
  styleUrl: './poi-list.component.scss',
})
export class PoiListComponent implements OnInit, OnChanges {
  readonly geoDataStore = inject(GeoDataStore);
  readonly widgetStore = inject(WidgetStore);
  @Input() searchWord = '';
  @Input() dataLayer!: DataLayer;
  @Input() onlyFiltered = false;
  @Input() activeKey = '';
  @Output() poiClicked = new EventEmitter<string>();

  pois: KeyValueDistricts | null = null;
  filteredList: FeatureProperties[] = [];

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * It is used for initializing the component's state or calling methods that depend on bound properties.
   * This method invokes the `setPois` function to perform component-specific logic.
   *
   * @return {void} No return value.
   */
  ngOnInit(): void {
    this.setPois();
  }

  /**
   * Lifecycle hook that is called when any data-bound property of a directive changes.
   * It triggers the process of setting points of interest and filtering them based on the current search word.
   *
   * @return {void} No return value.
   */
  ngOnChanges(): void {
    this.setPois();
    this.filterPois(this.searchWord);
  }

  /**
   * Sets the Points of Interest (POIs) based on the geoData available in the dataLayer.
   *
   * @return {void} This method does not return a value.
   */
  setPois() {
    this.pois = this.dataLayer.geoData
      ? this.geoDataStore.getDistrictsById(this.dataLayer.geoData)
      : null;
  }

  /**
   * Filters the points of interest (POIs) based on the provided search word.
   *
   * @param {string} searchWord - The word used to filter the POIs. Only POIs matching the search word will be included in the filtered list.
   * @return {void} This method does not return a value, but updates the filteredList property based on the filter operation.
   */
  filterPois(searchWord: string): void {
    if (!this.pois) {
      this.filteredList = [];
      return;
    }

    if (searchWord) {
      this.filteredList = filterFeaturesBySearchWordAsArray(
        this.pois,
        searchWord
      );
    } else {
      this.filteredList = Object.values(this.pois).sort((a, b) =>
        String(a.name).localeCompare(b.name)
      );
    }
  }

  poiClick(poi: FeatureProperties) {
    this.poiClicked.emit(poi.key);
  }

  toggleIsochrone(event: Event, poi: FeatureProperties) {
    event.stopPropagation();
    this.widgetStore.triggerEvent('toggleIsochrone', { key: poi.key });
  }

  isIsochroneActive(poi: FeatureProperties): boolean {
    const selected = this.widgetStore.selected();
    const activeIsochrones = selected?.widget?.map?.activeIsochrones || [];
    return activeIsochrones.includes(poi.key);
  }
}
